import { useState } from "react";
import { Plus } from "lucide-react";
import { useTenant } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Badge } from "@/components/ui/badge.js";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.js";
import { useToast } from "@/components/ui/toast.js";
import { DataTable } from "@/views/data-table.js";
import type { WebhookSubscription } from "@/lib/types.js";

export function WebhooksPage(): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const subs = useAsync(() => api.listWebhookSubscriptions(), [api]);
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Webhook subscriptions</h1>
        <Button onClick={() => setOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> New
        </Button>
      </div>
      {subs.loading ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        <DataTable<WebhookSubscription>
          rows={subs.data?.data ?? []}
          columns={[
            { header: "URL", cell: (s) => <code className="text-xs">{s.url}</code> },
            { header: "Events", cell: (s) => s.event_types.join(", ") },
            {
              header: "State",
              cell: (s) => (
                <Badge
                  variant={
                    s.state === "active" ? "success" : s.state === "paused" ? "warning" : "destructive"
                  }
                >
                  {s.state}
                </Badge>
              )
            },
            {
              header: "",
              cell: (s) => (
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      await api.testWebhookSubscription(s.id);
                      toast({ title: "Test event queued", variant: "success" });
                    }}
                  >
                    Test
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      await api.deleteWebhookSubscription(s.id);
                      subs.reload();
                    }}
                  >
                    Delete
                  </Button>
                </div>
              )
            }
          ]}
        />
      )}
      <NewWebhookDialog
        open={open}
        onOpenChange={setOpen}
        onCreated={() => {
          subs.reload();
          setOpen(false);
        }}
      />
    </div>
  );
}

function NewWebhookDialog({
  open,
  onOpenChange,
  onCreated
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New webhook subscription</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const url = String(form.get("url") ?? "");
            const events = String(form.get("event_types") ?? "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            const secret = String(form.get("secret") ?? "");
            setSubmitting(true);
            try {
              await api.createWebhookSubscription({ url, event_types: events, secret: secret || undefined });
              toast({ title: "Subscription created", variant: "success" });
              onCreated();
            } catch (error) {
              toast({ title: "Could not create", description: String(error), variant: "destructive" });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          <Field label="URL" name="url" required placeholder="https://example.com/calendar" />
          <Field
            label="Event types"
            name="event_types"
            placeholder="appointment.booked, payment.deposit_charged"
            defaultValue="appointment.booked,appointment.cancelled,calendar.sync_conflict"
          />
          <Field label="Secret (optional)" name="secret" placeholder="auto-generated if blank" />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  name,
  ...rest
}: { label: string; name: string } & React.InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={name}>{label}</Label>
      <Input id={name} name={name} {...rest} />
    </div>
  );
}
